// Content for the SEO + GEO Growth add-on service

export const SEO_GEO_INTRO = {
  eyebrow: 'Grow beyond launch',
  h2: 'BE FOUND ON GOOGLE—AND RECOMMENDED BY AI.',
  copy: [
    'Search is changing.',
    'Your prospective customers are not only using Google. They are increasingly asking AI platforms such as ChatGPT, Gemini and Perplexity to recommend businesses, explain services and compare potential providers.',
    'Boost!’s SEO + GEO Growth service combines traditional Search Engine Optimisation with Generative Engine Optimisation, helping your organisation build visibility across both conventional and AI-powered search.',
    'The programme continually creates useful search-focused content, improves website authority, identifies technical issues and monitors how the brand appears across emerging search platforms.',
  ],
  supporting: 'Available as an add-on to any Boost! website project or as a standalone monthly service.',
};

export const SEO_GEO_FEATURES = [
  { icon: 'Building2', label: 'Business and competitor analysis' },
  { icon: 'Search', label: 'Automated keyword discovery' },
  { icon: 'Network', label: 'Search-intent and topic clustering' },
  { icon: 'FileText', label: 'Up to 30 SEO and AI-optimised articles monthly' },
  { icon: 'Upload', label: 'Website content publishing' },
  { icon: 'Link2', label: 'Internal and external linking' },
  { icon: 'ExternalLink', label: 'Ongoing backlink acquisition' },
  { icon: 'Wrench', label: 'Technical site auditing' },
  { icon: 'Layers', label: 'Competitor and content-gap analysis' },
  { icon: 'Globe', label: 'Google visibility monitoring' },
  { icon: 'MessageSquare', label: 'ChatGPT citation tracking' },
  { icon: 'Sparkles', label: 'Gemini visibility monitoring' },
  { icon: 'Quote', label: 'Perplexity citation tracking' },
  { icon: 'MessagesSquare', label: 'Reddit brand-opportunity discovery' },
  { icon: 'BarChart3', label: 'Monthly performance reporting' },
  { icon: 'Compass', label: 'Boost! strategic oversight' },
];

export const SEO_GEO_PLANS = [
  {
    id: 'foundation',
    title: 'SEO + GEO Foundation',
    priceEx: '£595',
    priceInc: '£714',
    setupEx: '£395',
    setupInc: '£474',
    bestFor: 'Small businesses, local companies and organisations entering less competitive search markets.',
    includesLabel: 'Includes:',
    includes: [
      'Complete automated SEO and GEO growth engine',
      'Business and competitor onboarding',
      'Keyword and topic strategy',
      'Up to 30 search-focused articles monthly',
      'Automated content publishing',
      'Internal and external linking',
      'Ongoing backlink acquisition',
      'Technical website auditing',
      'AI citation monitoring',
      'Reddit opportunity discovery',
      'Monthly quality spot-checks',
      'Monthly performance summary',
      'Email support',
    ],
    cta: 'Choose Foundation',
  },
  {
    id: 'growth',
    title: 'SEO + GEO Growth',
    recommended: true,
    priceEx: '£995',
    priceInc: '£1,194',
    setupEx: '£595',
    setupInc: '£714',
    bestFor: 'Established businesses that want a managed organic-growth programme with regular human strategy and quality control.',
    includesLabel: 'Everything in Foundation, plus:',
    includes: [
      'Monthly strategy review',
      'Monthly 30-minute strategy call',
      'Human review of the content direction',
      'Editorial review of up to six priority articles',
      'Monthly competitor and content-gap review',
      'Up to two agreed on-page or technical improvements monthly',
      'Google Analytics and Search Console commentary',
      'AI visibility and citation commentary',
      'Enquiry and conversion recommendations',
      'Priority email support',
    ],
    cta: 'Choose Growth',
  },
  {
    id: 'authority',
    title: 'SEO + GEO Authority',
    priceEx: '£1,495',
    priceInc: '£1,794',
    setupEx: '£795',
    setupInc: '£954',
    bestFor: 'Competitive sectors, ecommerce companies and businesses pursuing stronger national or sector-wide visibility.',
    includesLabel: 'Everything in Growth, plus:',
    includes: [
      'Expanded editorial quality control',
      'Priority review of commercially important content',
      'Up to four key page improvements monthly',
      'Deeper technical SEO management',
      'Wider topical-authority planning',
      'Custom AI prompt-monitoring set',
      'Reddit participation recommendations',
      'Conversion-path improvements',
      'Advanced internal-linking work',
      'Monthly strategy meeting',
      'Quarterly search-growth roadmap',
    ],
    cta: 'Choose Authority',
  },
];

export const SEO_GEO_COMPARISON = [
  { plan: 'Foundation', monthlyEx: '£595', monthlyInc: '£714', setupEx: '£395', recommendedFor: 'Local and smaller businesses' },
  { plan: 'Growth', monthlyEx: '£995', monthlyInc: '£1,194', setupEx: '£595', recommendedFor: 'Established growing businesses', recommended: true },
  { plan: 'Authority', monthlyEx: '£1,495', monthlyInc: '£1,794', setupEx: '£795', recommendedFor: 'Competitive, national and ecommerce businesses' },
];

export const SEO_GEO_STEPS = [
  { step: '1', title: 'Analyse', copy: 'Boost! reviews the business, website, audience, competitors and commercial priorities.' },
  { step: '2', title: 'Plan', copy: 'A search and content strategy is created around relevant topics, questions and customer intent.' },
  { step: '3', title: 'Create', copy: 'Optimised content is produced around the agreed strategy and prepared for publication.' },
  { step: '4', title: 'Build authority', copy: 'The programme strengthens internal linking, external authority signals and technical site health.' },
  { step: '5', title: 'Measure and refine', copy: 'Google performance, website activity and AI visibility signals are monitored and used to refine future activity.' },
];

export const SEO_GEO_EXPLAIN = {
  h2: 'SEO MEETS THE NEW GENERATION OF SEARCH.',
  panels: [
    {
      title: 'Traditional SEO',
      copy: [
        'Traditional Search Engine Optimisation improves how search engines such as Google crawl, understand and rank your website.',
        'The work focuses on content relevance, keyword opportunities, website authority, technical quality and the experience delivered to visitors.',
      ],
    },
    {
      title: 'Generative Engine Optimisation',
      copy: [
        'Generative Engine Optimisation helps AI-powered search and answer platforms understand your brand, expertise, products and services.',
        'The objective is to increase the likelihood that the business is discovered, referenced or cited when prospective customers ask relevant questions.',
      ],
    },
  ],
  combined:
    'SEO and GEO work together. Strong technical foundations, trustworthy content and genuine authority remain central to visibility across both conventional and AI-powered search.',
};

export const SEO_GEO_EXPECTATIONS = {
  h2: 'ORGANIC GROWTH COMPOUNDS OVER TIME.',
  copy: [
    'SEO and GEO are long-term growth activities.',
    'Performance depends on factors including the competitiveness of the market, the website’s existing authority, technical condition, content quality, customer demand and the behaviour of external search platforms.',
    'For this reason, Boost! does not guarantee specific rankings, traffic volumes, lead numbers or placement within AI-generated answers.',
    'We recommend an initial commitment of at least six months so the programme has sufficient time to publish content, build authority, gather performance data and refine its direction.',
  ],
};

export const SEO_GEO_QUALITY = {
  h2: 'AUTOMATION SUPPORTED BY HUMAN JUDGEMENT.',
  copy: [
    'Technology allows the programme to research, create and publish content at a scale that would previously have required a large editorial team.',
    'Boost! adds the strategic and human layer.',
    'Depending on the selected plan, this can include content-direction review, quality spot-checking, editorial review, competitor analysis, technical improvements and commercial recommendations.',
    'Clients operating in regulated or highly specialist industries may require an enhanced editorial, legal, medical or compliance-review process. This will be assessed and quoted separately.',
  ],
};

export const SEO_GEO_FAQS = [
  {
    q: 'What is GEO?',
    a: 'GEO stands for Generative Engine Optimisation. It focuses on helping AI-powered platforms understand, reference and recommend a business when users ask relevant questions.',
  },
  {
    q: 'Is GEO replacing SEO?',
    a: 'No. Strong GEO depends heavily on established SEO principles such as accessible websites, useful content, trusted information, authority and technical quality. The two disciplines work together.',
  },
  {
    q: 'How much content will be created?',
    a: 'The service can create and publish up to 30 SEO and AI-optimised articles per month. The exact content direction is based on the business, audience, search opportunities and selected service level.',
  },
  {
    q: 'Will every article be manually written?',
    a: 'The programme uses advanced technology to research, structure and generate content efficiently. Boost!’s level of manual strategy, review and editing depends on the selected plan.',
  },
  {
    q: 'Can you guarantee that ChatGPT will recommend my company?',
    a: 'No search provider can responsibly guarantee a particular ranking, recommendation or AI citation. AI-generated responses can vary according to the platform, prompt, user and timing.',
  },
  {
    q: 'How soon will we see results?',
    a: 'Some early visibility changes may appear relatively quickly, while meaningful organic growth commonly requires several months of consistent activity. Results depend on the website’s starting point and the competitiveness of the market.',
  },
  {
    q: 'Do I need a new website to use the service?',
    a: 'No. SEO + GEO Growth can be added to a new Boost! website or applied to a suitable existing website following an initial review.',
  },
  {
    q: 'Is there a minimum term?',
    a: 'The recommended initial commitment is six months. After the initial term, the service continues monthly with 30 days’ notice.',
  },
];

export const GROWTH_PROJECT_TYPES = [
  'SEO and organic search',
  'GEO and AI-search visibility',
  'Website plus SEO and GEO',
  'Existing website growth programme',
];

export const MARKETING_INVESTMENT_OPTIONS = [
  'Under £500',
  '£500–£1,000',
  '£1,000–£2,000',
  '£2,000–£5,000',
  '£5,000+',
  'Not sure yet',
];

export const BUNDLE_PACKAGES = [
  { id: 'launchpad', label: 'Launchpad Website', priceEx: 1750 },
  { id: 'business', label: 'Business Website', priceEx: 2995, popular: true },
  { id: 'business-plus', label: 'Business Plus', priceEx: 4495 },
  { id: 'ecommerce', label: 'Ecommerce Website', priceEx: 5995 },
];

export const BUNDLE_PLANS = [
  { id: 'foundation', label: 'Foundation', monthlyEx: 595, setupEx: 395 },
  { id: 'growth', label: 'Growth', monthlyEx: 995, setupEx: 595, recommended: true },
  { id: 'authority', label: 'Authority', monthlyEx: 1495, setupEx: 795 },
];
